'use client';

import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import Link from 'next/link';
import { Menu, Search, X } from 'lucide-react';
import type { DocNavigationItem } from './lib';
import styles from './docs.module.css';

interface DocsSidebarProps {
  activeSlug: string;
  navigation: DocNavigationItem[];
}

export function DocsSidebar({ activeSlug, navigation }: DocsSidebarProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [isCompact, setIsCompact] = useState(false);

  useEffect(() => setOpen(false), [activeSlug]);

  useEffect(() => {
    const media = window.matchMedia('(max-width: 900px)');
    const updateViewport = () => setIsCompact(media.matches);
    updateViewport();
    media.addEventListener('change', updateViewport);
    return () => media.removeEventListener('change', updateViewport);
  }, []);

  const visibleItems = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return navigation;

    return navigation.filter(
      (item) =>
        !item.isSection && item.title.toLowerCase().includes(normalizedQuery)
    );
  }, [navigation, query]);

  return (
    <>
      <div className={styles.mobileToolbar}>
        <button
          type='button'
          className={styles.mobileMenuButton}
          onClick={() => setOpen(true)}
          aria-label='Open documentation navigation'
        >
          <Menu size={18} />
          Browse docs
        </button>
        <span className={styles.mobileSection}>SFI / DOCS</span>
      </div>

      {open && (
        <button
          type='button'
          className={styles.sidebarBackdrop}
          onClick={() => setOpen(false)}
          aria-label='Close documentation navigation'
        />
      )}

      <aside
        className={`${styles.sidebar} ${open ? styles.sidebarOpen : ''}`}
        aria-label='Documentation navigation'
        aria-hidden={isCompact && !open}
        inert={isCompact && !open ? true : undefined}
      >
        <div className={styles.sidebarHeader}>
          <Link href='/docs' className={styles.docsBrand}>
            <span className={styles.docsBrandMark}>S</span>
            <span>
              <strong>Singularity</strong>
              <small>Documentation</small>
            </span>
          </Link>
          <button
            type='button'
            className={styles.sidebarClose}
            onClick={() => setOpen(false)}
            aria-label='Close documentation navigation'
          >
            <X size={18} />
          </button>
        </div>

        <label className={styles.searchBox}>
          <Search size={16} aria-hidden='true' />
          <span className={styles.srOnly}>Search documentation</span>
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder='Search pages'
          />
          <kbd>/</kbd>
        </label>

        <nav className={styles.navigationList}>
          {visibleItems.map((item, index) => {
            if (item.isSection) {
              return (
                <p
                  className={styles.navigationSection}
                  key={`${item.title}-${index}`}
                >
                  {item.title}
                </p>
              );
            }

            const active = item.slug === activeSlug;
            return (
              <Link
                key={item.href}
                href={item.href!}
                aria-current={active ? 'page' : undefined}
                className={`${styles.navigationLink} ${
                  active ? styles.navigationLinkActive : ''
                }`}
                style={
                  {
                    '--navigation-depth': Math.min(item.depth, 3),
                  } as CSSProperties
                }
              >
                <span className={styles.navigationIndicator} />
                <span>{item.title}</span>
              </Link>
            );
          })}

          {visibleItems.length === 0 && (
            <p className={styles.noResults}>No pages match “{query}”.</p>
          )}
        </nav>

        <div className={styles.sidebarFooter}>
          <span>{navigation.filter((item) => item.href).length} pages</span>
          <span>v1.0</span>
        </div>
      </aside>
    </>
  );
}
